import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { useEvent, useEventAction } from "./EventContext";
import { useUX } from "./UXContext";
import api from '../api/axios'

export const ViewContext = createContext();

export const useView = () => useContext(ViewContext);

const ADVANCE_RETRY_DELAYS_MS = [400, 800, 1600];

export const ViewProvider = ({ children, learningJourneyId }) => {
    const [view, setView] = useState(null);
    const [error, setError] = useState(null);
    const [readOnlyOverride, setReadOnlyOverride] = useState(false);
    const [advancing, setAdvancing] = useState(false);
    const pollingRef = useRef(null);
    const onAdvanceRef = useRef(null);
    const lastViewIdRef = useRef(null);
    const { emitEvent } = useEvent();
    const { profileRevision } = useUX();
    const handledRevisionRef = useRef(0);

    // Lets the view currently on screen register an action that must complete
    // before the user is allowed to move to the next view. Stable identity
    // (useCallback, no deps) so it's safe as an effect dependency elsewhere —
    // it only ever writes to a ref.
    const setOnAdvance = useCallback((fn) => {
        onAdvanceRef.current = fn;
    }, []);

    // Reading a view and adopting one are separate concerns. A caller that only needs
    // to *ask* what the server now says -- deciding how a flow ends, say -- must not
    // also re-broadcast that view's controls to everything listening.
    const readView = useCallback(async () => {
        const { data } = await api.post(`/workspace/current-view/`, {
            learning_journey_id: learningJourneyId
        });
        return data;
    }, [learningJourneyId]);

    const stopPoll = useCallback(() => {
        if (pollingRef.current) {
            clearTimeout(pollingRef.current);
            pollingRef.current = null;
        }
    }, []);

    const fetchView = useCallback(async () => {
        try {
            const data = await readView();
            setView(data);
            data.controls.forEach(control => emitEvent(control));
            return data;
        } catch (err) {
            console.error("Failed to fetch view:", err);
            if (err.response && [404, 500].includes(err.response.status)) {
                stopPoll();
                setError(err.response.status);
            }
            return null;
        }
    }, [readView, emitEvent, stopPoll]);

    const startPoll = useCallback(() => {
        let interval = 1000;
        let timer;
        const poll = async () => {
            await fetchView();
            // stopPoll() (or a newer chain) may have run while this fetch was in flight;
            // rescheduling then would leave an orphan chain no stopPoll() can reach.
            if (pollingRef.current !== timer) return;
            interval = Math.min(interval * 2, 8000); // exponential backoff
            timer = setTimeout(poll, interval);
            pollingRef.current = timer;
        };

        timer = setTimeout(poll, interval);
        pollingRef.current = timer;
    }, [fetchView]);

    const advance = useCallback(async () => {
        const fn = onAdvanceRef.current;
        onAdvanceRef.current = null;
        const beforeViewId = view?.view_id;

        setAdvancing(true);
        try {
            if (fn) await fn();

            let data = await fetchView();
            for (const delay of ADVANCE_RETRY_DELAYS_MS) {
                if (!data || data.view_id !== beforeViewId) break;
                await new Promise(resolve => setTimeout(resolve, delay));
                data = await fetchView();
            }
        } finally {
            setAdvancing(false);
        }
    }, [fetchView, view]);

    // Keyed on whether to poll, not on the view: each poll adopts a new view object, and an
    // effect on it would restart the chain at 1s every time, so the backoff never grew.
    const shouldPoll = !view || view.way_forward === "wait";
    useEffect(() => {
        if (shouldPoll) startPoll();
        return () => stopPoll();
    }, [shouldPoll, startPoll, stopPoll]);


    useEffect(() => {
        if (learningJourneyId) {
            fetchView();
        }
    }, [learningJourneyId, fetchView]);

    // A view's schema is derived from the user's UX profile as much as from the
    // journey's state. A settled view is no longer polled, so joining the beta 
    // from the settings modal would otherwise stay invisible until a reload. 
    useEffect(() => {
        if (profileRevision === handledRevisionRef.current) return;
        handledRevisionRef.current = profileRevision;
        fetchView();
    }, [profileRevision, fetchView]);

    // Each view's read_only fully describes the panel's lock state on that view,
    // but only on transition -- a mid-conversation "read-only" control (fired
    // e.g. once a PF quiz's tutor session closes) must not be cleared again by a
    // later poll of the same view, and the override itself must not leak into
    // the next view once the student actually moves on.
    useEffect(() => {
        if (!view) return;
        if (view.view_id !== lastViewIdRef.current) {
            lastViewIdRef.current = view.view_id;
            setReadOnlyOverride(false);
        }
    }, [view]);

    useEventAction("read-only", useCallback(() => setReadOnlyOverride(true), []));
    useEventAction("refresh-view", fetchView);

    const readOnly = Boolean(view?.chat?.read_only) || readOnlyOverride;
    const closedNoticeText = view?.chat?.closed_notice_text ?? null;

    return (
        <ViewContext.Provider value={{
            learningJourneyId, view, error, readView, setView, setOnAdvance, advance, advancing, readOnly,
            closedNoticeText, refreshView: fetchView,
        }}>
            {children}
        </ViewContext.Provider>
    );
};